import { useEffect, useRef, useState } from 'react';
import { logToRemote } from '@/utils/remoteLogger';

const API_URL = import.meta.env.VITE_API_URL as string;

export const HealthCheck = () => {
  const lastHeartbeat = useRef(Date.now());
  const checkInterval = useRef<NodeJS.Timeout>();
  const lastReload = useRef(Date.now());
  const [errorCount, setErrorCount] = useState(0);
  const errorCountRef = useRef(0);

  const RELOAD_COOLDOWN = 60000; // 1 minuto entre reloads

  useEffect(() => {
    // Actualizar heartbeat cada 5 segundos
    const heartbeatInterval = setInterval(() => {
      lastHeartbeat.current = Date.now();
    }, 5000);

    // Ping a health endpoint cada 30 segundos
    const healthPingInterval = setInterval(async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const res = await fetch(`${API_URL}/health`, { signal: controller.signal });
        clearTimeout(timeoutId);
        const data = await res.json();
        if (!data?.status || data.status !== 'ok') {
          throw new Error('Health check returned invalid status');
        }
      } catch (error) {
        console.error('❌ Health ping failed:', error);
        errorCountRef.current++;
        setErrorCount(errorCountRef.current);
      }
    }, 30000);

    // Verificar que la app sigue viva cada 15 segundos
    checkInterval.current = setInterval(() => {
      const timeSinceLastBeat = Date.now() - lastHeartbeat.current;
      const timeSinceLastReload = Date.now() - lastReload.current;
      
      // Si han pasado más de 30 segundos sin heartbeat, algo está mal
      if (timeSinceLastBeat > 30000) {
        console.error('🔴 App no responde, preparando reload...');
        checkReloadConditions('app_freeze');
      }
      
      // Si hay más de 5 errores en los últimos 60 segundos
      if (errorCountRef.current > 5 && timeSinceLastReload > RELOAD_COOLDOWN) {
        console.error('🔴 Múltiples errores detectados, preparando reload...');
        checkReloadConditions('multiple_errors');
      }
    }, 15000);

    // Monitor errores globales
    const handleError = (event: ErrorEvent) => {
      errorCountRef.current++;
      setErrorCount(errorCountRef.current);
      
      logToRemote('error', {
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }, 'error');
    };

    window.addEventListener('error', handleError);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(healthPingInterval);
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      window.removeEventListener('error', handleError);
    };
  }, []);

  const checkReloadConditions = (reason: string) => {
    const timeSinceLastReload = Date.now() - lastReload.current;
    
    if (timeSinceLastReload < RELOAD_COOLDOWN) {
      console.warn('⏳ En cooldown, no se recarga aún. Faltan', 
        Math.round((RELOAD_COOLDOWN - timeSinceLastReload) / 1000), 'segundos');
      return;
    }

    // Log antes de recargar
    const uptimeSeconds = Math.floor((Date.now() - lastReload.current) / 1000);
    
    logToRemote('auto_reload', {
      reason,
      errorCount: errorCountRef.current,
      uptime_seconds: uptimeSeconds,
      url: window.location.href
    }, 'critical');

    console.error('🔄 Recargando aplicación. Razón:', reason);
    
    // Dar tiempo para que se envíe el log
    setTimeout(() => {
      lastReload.current = Date.now();
      window.location.reload();
    }, 1000);
  };

  return null; // Componente invisible
};
